import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useNavigate } from "react-router-dom";
import { Modal } from "components/ui/Modal";

const PlatformAuditLogs: React.FC = () => {
 const { t } = useTranslation();
 const { isAuthenticated } = usePlatformAuth();
 const navigate = useNavigate();
 const [logs, setLogs] = useState<any[]>([]);
 const [loading, setLoading] = useState(true);
 const [page, setPage] = useState(1);
 const [limit] = useState(50);
 const [total, setTotal] = useState(0);
 const [pages, setPages] = useState(0);
 
 // Filters
 const [search, setSearch] = useState("");
 const [debouncedSearch, setDebouncedSearch] = useState("");
 const [externalService, setExternalService] = useState("");
 const [action, setAction] = useState("");
 const [entityType, setEntityType] = useState("");
 const [startDate, setStartDate] = useState("");
 const [endDate, setEndDate] = useState("");
 const [selectedLog, setSelectedLog] = useState<any | null>(null);
 const [isModalOpen, setIsModalOpen] = useState(false);

 // Debounce search input
 const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
 const searchInputRef = useRef<HTMLInputElement>(null);
 const wasSearchFocusedRef = useRef(false);

 // Memoize fetchLogs to prevent recreation on every render
 const fetchLogs = useCallback(async () => {
  try {
   setLoading(true);
   const params: any = {
    page,
    limit,
   };

   if (debouncedSearch) params.search = debouncedSearch;
   if (externalService) params.externalService = externalService;
   if (action) params.action = action;
   if (entityType) params.entityType = entityType;
   if (startDate) params.startDate = startDate;
   if (endDate) params.endDate = endDate;

   const response = await platformApiService.getAuditLogs(params);
   setLogs(response.data.logs || []);
   setTotal(response.data.pagination?.total || 0);
   setPages(response.data.pagination?.pages || 0);
  } catch (error) {
   console.error("Error fetching audit logs:", error);
  } finally {
   setLoading(false);
  }
 }, [page, limit, debouncedSearch, externalService, action, entityType, startDate, endDate]);

 // Debounce search input - don't reset page here to avoid re-renders
 useEffect(() => {
  if (searchTimeoutRef.current) {
   clearTimeout(searchTimeoutRef.current);
  }

  searchTimeoutRef.current = setTimeout(() => {
   setDebouncedSearch(search);
   // Reset page only when debounced search changes
   setPage(1);
  }, 500);

  return () => {
   if (searchTimeoutRef.current) {
    clearTimeout(searchTimeoutRef.current);
   }
  };
 }, [search]);

 // Fetch logs when filters change
 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  fetchLogs();
 }, [isAuthenticated, navigate, fetchLogs]);

 // Memoize onChange handlers to prevent re-renders
 const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
  setSearch(e.target.value);
  // Don't reset page here - let debounce handle it
 }, []);

 // Maintain focus on search input
 useEffect(() => {
  if (wasSearchFocusedRef.current && searchInputRef.current && document.activeElement !== searchInputRef.current) {
   // Restore cursor position if possible
   const cursorPosition = searchInputRef.current.selectionStart || search.length;
   searchInputRef.current.focus();
   searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
  }
 }, [search]);

 // Track focus state
 const handleSearchFocus = useCallback(() => {
  wasSearchFocusedRef.current = true;
 }, []);

 const handleSearchBlur = useCallback(() => {
  // Only mark as not focused if we're not clicking on something that should maintain focus
  setTimeout(() => {
   if (document.activeElement !== searchInputRef.current) {
    wasSearchFocusedRef.current = false;
   }
  }, 0);
 }, []);

 const handleExternalServiceChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
  setExternalService(e.target.value);
  setPage(1);
 }, []);

 const handleActionChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
  setAction(e.target.value);
  setPage(1);
 }, []);

 const handleEntityTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
  setEntityType(e.target.value);
  setPage(1);
 }, []);

 const handleStartDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
  setStartDate(e.target.value);
  setPage(1);
 }, []);

 const handleEndDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
  setEndDate(e.target.value);
  setPage(1);
 }, []);

 const handleReset = useCallback(() => {
  setSearch("");
  setExternalService("");
  setAction("");
  setEntityType("");
  setStartDate("");
  setEndDate("");
  setPage(1);
 }, []);

 const getExternalServiceBadge = (log: any) => {
  const service = log.metadata?.externalService;
  if (!service) return null;

  const colors: Record<string, string> = {
   stripe: "bg-purple-100 text-purple-800",
   namecheap: "bg-blue-100 text-blue-800",
   smtp: "bg-green-100 text-green-800",
  };

  return (
   <span className={`px-2 py-1 rounded text-xs font-medium ${colors[service] || "bg-gray-100 text-gray-800"}`}>
    {service.toUpperCase()}
   </span>
  );
 };

 const handleViewLog = (log: any) => {
  setSelectedLog(log);
  setIsModalOpen(true);
 };

 const handleCloseModal = () => {
  setIsModalOpen(false);
  setSelectedLog(null);
 };

 if (loading && logs.length === 0) {
  return <div className="p-8">Loading audit logs...</div>;
 }

 return (
  <div className="p-8">
   <div className="flex justify-between items-center mb-6">
    <h1 className="text-3xl font-bold">Audit Logs</h1>
    <div className="text-sm text-gray-500">
     Total: {total} logs
    </div>
   </div>

   {/* Filters */}
   <div className="bg-white shadow p-6 mb-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
     <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
       Search
      </label>
      <input
       ref={searchInputRef}
       type="text"
       value={search}
       onChange={handleSearchChange}
       onFocus={handleSearchFocus}
       onBlur={handleSearchBlur}
       placeholder={t("common.placeholders.searchActions")}
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
       External Service
      </label>
      <select
       value={externalService}
       onChange={handleExternalServiceChange}
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
       <option value="">All Services</option>
       <option value="stripe">Stripe</option>
       <option value="namecheap">Namecheap</option>
       <option value="smtp">Email (SMTP)</option>
      </select>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
       Action
      </label>
      <select
       value={action}
       onChange={handleActionChange}
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
       <option value="">All Actions</option>
       <optgroup label="Stripe - Payments">
        <option value="stripe.payment_intent.created">Payment Intent Created</option>
        <option value="stripe.payment_intent.confirmed">Payment Intent Confirmed</option>
        <option value="stripe.payment_intent.status_retrieved">Payment Status Retrieved</option>
        <option value="stripe.refund.created">Refund Created</option>
       </optgroup>
       <optgroup label="Stripe - Subscriptions">
        <option value="stripe.customer.created">Customer Created</option>
        <option value="stripe.subscription.created">Subscription Created</option>
        <option value="stripe.subscription.updated">Subscription Updated</option>
        <option value="stripe.subscription.cancelled">Subscription Cancelled</option>
        <option value="stripe.plugin_subscription.created">Plugin Subscription Created</option>
        <option value="stripe.plugin_subscription.cancelled">Plugin Subscription Cancelled</option>
       </optgroup>
       <optgroup label="Stripe - Products & Pricing">
        <option value="stripe.product.created">Product Created</option>
        <option value="stripe.price.created">Price Created</option>
        <option value="stripe.checkout_session.created">Checkout Session Created</option>
       </optgroup>
       <optgroup label="Stripe - Webhooks">
        <option value="stripe.webhook.received">Webhook Received</option>
        <option value="stripe.webhook.subscription.created">Webhook: Subscription Created</option>
        <option value="stripe.webhook.subscription.updated">Webhook: Subscription Updated</option>
        <option value="stripe.webhook.subscription.deleted">Webhook: Subscription Deleted</option>
        <option value="stripe.webhook.invoice.paid">Webhook: Invoice Paid</option>
        <option value="stripe.webhook.invoice.payment_failed">Webhook: Payment Failed</option>
        <option value="stripe.webhook.invoice.payment_action_required">Webhook: Payment Action Required</option>
       </optgroup>
       <optgroup label="Namecheap - DNS">
        <option value="namecheap.dns.subdomain.created">Subdomain Created</option>
        <option value="namecheap.dns.subdomain.deleted">Subdomain Deleted</option>
       </optgroup>
       <optgroup label="Email - SMTP">
        <option value="email.sent">Email Sent</option>
        <option value="email.send_failed">Email Send Failed</option>
       </optgroup>
      </select>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
       Entity Type
      </label>
      <select
       value={entityType}
       onChange={handleEntityTypeChange}
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
       <option value="">All Entity Types</option>
       <option value="ExternalAPI">External API</option>
       <option value="Product">Product</option>
       <option value="Order">Order</option>
       <option value="Store">Store</option>
       <option value="Subscription">Subscription</option>
       <option value="User">User</option>
       <option value="Payment">Payment</option>
       <option value="Invoice">Invoice</option>
       <option value="Plugin">Plugin</option>
       <option value="Plan">Plan</option>
      </select>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
       Start Date
      </label>
      <input
       type="date"
       value={startDate}
       onChange={handleStartDateChange}
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
       End Date
      </label>
      <input
       type="date"
       value={endDate}
       onChange={handleEndDateChange}
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
     </div>
    </div>

    <button
     onClick={handleReset}
     className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200"
    >
     Reset Filters
    </button>
   </div>

   {/* Table */}
   <div className="bg-white shadow overflow-hidden">
    <div className="overflow-x-auto">
     <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
       <tr>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Timestamp
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Service
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Action
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Entity
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         User
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Store
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
         Details
        </th>
       </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
       {logs.length === 0 ? (
        <tr>
         <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
          No audit logs found
         </td>
        </tr>
       ) : (
        logs.map((log) => (
         <tr key={log._id} className="hover:bg-gray-50">
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
           {new Date(log.timestamp).toLocaleString()}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm">
           {getExternalServiceBadge(log)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
           {log.action}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
           {log.entityType}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
           {log.userId ? (log.userId as any)?.name : (log.platformUserId as any)?.name || "System"}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
           {log.storeId ? (log.storeId as any)?.name : "Platform"}
          </td>
          <td className="px-6 py-4 text-sm text-gray-500">
           <div className="flex items-center gap-2">
            {log.metadata?.apiMethod && (
             <span className="text-xs text-gray-400">
              {log.metadata.apiMethod}
             </span>
            )}
            <button
             onClick={() => handleViewLog(log)}
             className="px-3 py-1 text-xs font-medium text-indigo-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
             View
            </button>
           </div>
          </td>
         </tr>
        ))
       )}
      </tbody>
     </table>
    </div>

    {/* Pagination */}
    {pages > 1 && (
     <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
      <div className="text-sm text-gray-700">
       Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} results
      </div>
      <div className="flex space-x-2">
       <button
        onClick={() => setPage(page - 1)}
        disabled={page === 1}
        className={`px-4 py-2 text-sm font-medium ${
         page === 1
          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
        }`}
       >
        Previous
       </button>
       <div className="flex space-x-1">
        {Array.from({ length: Math.min(5, pages) }, (_, i) => {
         let pageNum = 0;
         if (pages <= 5) {
          pageNum = i + 1;
         } else if (page <= 3) {
          pageNum = i + 1;
         } else if (page >= pages - 2) {
          pageNum = pages - 4 + i;
         } else {
          pageNum = page - 2 + i;
         }
         return (
          <button
           key={pageNum}
           onClick={() => setPage(pageNum)}
           className={`px-3 py-2 text-sm font-medium ${
            page === pageNum
             ? "bg-blue-600 text-white"
             : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
           }`}
          >
           {pageNum}
          </button>
         );
        })}
       </div>
       <button
        onClick={() => setPage(page + 1)}
        disabled={page === pages}
        className={`px-4 py-2 text-sm font-medium ${
         page === pages
          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
        }`}
       >
        Next
       </button>
      </div>
     </div>
    )}
   </div>

   {/* Audit Log Detail Modal */}
   <Modal
    isOpen={isModalOpen}
    onClose={handleCloseModal}
    title={`Audit Log Details - ${selectedLog?.action || ""}`}
    size="xl"
   >
    {selectedLog && (
     <div className="space-y-4">
      <div className="bg-gray-50 p-4">
       <h4 className="text-sm font-semibold text-gray-700 mb-2">Full JSON</h4>
       <pre className="bg-gray-900 text-gray-100 p-4 overflow-x-auto text-xs">
        {JSON.stringify(selectedLog, null, 2)}
       </pre>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
       <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Basic Info</h4>
        <dl className="space-y-1 text-sm">
         <div className="flex justify-between">
          <dt className="text-gray-500">Action:</dt>
          <dd className="text-gray-900 font-medium">{selectedLog.action}</dd>
         </div>
         <div className="flex justify-between">
          <dt className="text-gray-500">Entity Type:</dt>
          <dd className="text-gray-900">{selectedLog.entityType}</dd>
         </div>
         <div className="flex justify-between">
          <dt className="text-gray-500">Entity ID:</dt>
          <dd className="text-gray-900 font-mono text-xs">{selectedLog.entityId || "N/A"}</dd>
         </div>
         <div className="flex justify-between">
          <dt className="text-gray-500">Timestamp:</dt>
          <dd className="text-gray-900">{new Date(selectedLog.timestamp).toLocaleString()}</dd>
         </div>
        </dl>
       </div>

       <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Context</h4>
        <dl className="space-y-1 text-sm">
         {selectedLog.storeId && (
          <div className="flex justify-between">
           <dt className="text-gray-500">Store:</dt>
           <dd className="text-gray-900">
            {typeof selectedLog.storeId === "object" 
             ? selectedLog.storeId.name || selectedLog.storeId._id 
             : selectedLog.storeId}
           </dd>
          </div>
         )}
         {selectedLog.userId && (
          <div className="flex justify-between">
           <dt className="text-gray-500">User:</dt>
           <dd className="text-gray-900">
            {typeof selectedLog.userId === "object" 
             ? selectedLog.userId.name || selectedLog.userId._id 
             : selectedLog.userId}
           </dd>
          </div>
         )}
         {selectedLog.platformUserId && (
          <div className="flex justify-between">
           <dt className="text-gray-500">Platform User:</dt>
           <dd className="text-gray-900">
            {typeof selectedLog.platformUserId === "object" 
             ? selectedLog.platformUserId.name || selectedLog.platformUserId._id 
             : selectedLog.platformUserId}
           </dd>
          </div>
         )}
         {selectedLog.ipAddress && (
          <div className="flex justify-between">
           <dt className="text-gray-500">IP Address:</dt>
           <dd className="text-gray-900 font-mono text-xs">{selectedLog.ipAddress}</dd>
          </div>
         )}
        </dl>
       </div>
      </div>

      {selectedLog.changes && (
       <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Changes</h4>
        <div className="grid grid-cols-2 gap-4">
         {selectedLog.changes.before && (
          <div>
           <h5 className="text-xs font-medium text-red-600 mb-1">Before</h5>
           <pre className="bg-red-50 border border-red-200 text-red-900 p-3 overflow-x-auto text-xs max-h-48 overflow-y-auto">
            {JSON.stringify(selectedLog.changes.before, null, 2)}
           </pre>
          </div>
         )}
         {selectedLog.changes.after && (
          <div>
           <h5 className="text-xs font-medium text-green-600 mb-1">After</h5>
           <pre className="bg-green-50 border border-green-200 text-green-900 p-3 overflow-x-auto text-xs max-h-48 overflow-y-auto">
            {JSON.stringify(selectedLog.changes.after, null, 2)}
           </pre>
          </div>
         )}
        </div>
       </div>
      )}

      {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
       <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Metadata</h4>
        <pre className="bg-blue-50 border border-blue-200 text-blue-900 p-3 overflow-x-auto text-xs max-h-48 overflow-y-auto">
         {JSON.stringify(selectedLog.metadata, null, 2)}
        </pre>
       </div>
      )}
     </div>
    )}
   </Modal>
  </div>
 );
};

export default PlatformAuditLogs;

